import { useEffect, useMemo, useState } from "react";
import { adminRequest } from "../api";
import { warrantySurvey } from "../survey";

type Summary = {
  totals: {
    landingVisits: number;
    uniqueVisits: number;
    starts: number;
    completions: number;
    abandoned: number;
    completionRate: number;
    demoStarts: number;
    giftClaims: number;
    followUpLeads: number;
  };
  byVariant: Array<{ variant: string; visits: number; starts: number; completions: number; completionRate: number }>;
  dropoff: Array<{ questionId: string; views: number; answers: number; skips: number }>;
  reactions: Array<{ questionId: string; answer: string; count: number }>;
  interests: Array<{ answer: string; count: number }>;
  demo: Array<{ milestone: string; count: number }>;
};

type Respondent = {
  session_id: string;
  variant: string;
  status: string;
  created_at: string;
  last_seen_at: string;
  furthest_step: number;
  last_step: number;
  completed_at: string | null;
};

type Contact = {
  session_id: string;
  name: string;
  company: string;
  follow_up_email: string;
  phone: string;
  preferred_method: string;
  research_consent: number;
  marketing_consent: number;
  updated_at: string;
};

type Gift = {
  id: number;
  session_id: string;
  name: string;
  company: string;
  gift_email: string;
  status: string;
  created_at: string;
  fulfilled_at: string | null;
};

type ResponseRow = {
  session_id: string;
  variant: string;
  question_id: string;
  answer_json: string | null;
  is_skipped: number;
  revision_count: number;
  updated_at: string;
};

type JourneyRow = {
  event_name: string;
  question_id: string | null;
  step_index: number | null;
  metadata_json: string;
  occurred_at: string;
};

const metricLabels: Record<keyof Summary["totals"], string> = {
  landingVisits: "Landing visits",
  uniqueVisits: "Unique respondents",
  starts: "Survey starts",
  completions: "Completions",
  abandoned: "Abandoned",
  completionRate: "Completion rate",
  demoStarts: "Demo starts",
  giftClaims: "Gift claims",
  followUpLeads: "Follow-up leads",
};

export function AdminDashboard() {
  const [tab, setTab] = useState<"overview" | "respondents" | "responses" | "contacts" | "gifts">("overview");
  const [summary, setSummary] = useState<Summary | null>(null);
  const [respondents, setRespondents] = useState<Respondent[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [gifts, setGifts] = useState<Gift[]>([]);
  const [responses, setResponses] = useState<ResponseRow[]>([]);
  const [journey, setJourney] = useState<{ sessionId: string; rows: JourneyRow[] } | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [localKey, setLocalKey] = useState("");

  useEffect(() => {
    Promise.all([
      adminRequest<Summary>("/summary"),
      adminRequest<{ respondents: Respondent[] }>("/respondents"),
      adminRequest<{ contacts: Contact[] }>("/contacts"),
      adminRequest<{ gifts: Gift[] }>("/gifts"),
      adminRequest<{ responses: ResponseRow[] }>("/responses"),
    ]).then(([summaryData, respondentData, contactData, giftData, responseData]) => {
      setSummary(summaryData);
      setRespondents(respondentData.respondents);
      setContacts(contactData.contacts);
      setGifts(giftData.gifts);
      setResponses(responseData.responses);
    }).catch((reason: Error) => setError(reason.message)).finally(() => setLoading(false));
  }, []);

  const filteredRespondents = useMemo(() => respondents.filter((item) =>
    item.session_id.toLowerCase().includes(search.toLowerCase()) || item.variant.toLowerCase() === search.toLowerCase() || item.status.toLowerCase().includes(search.toLowerCase()),
  ), [respondents, search]);

  const openJourney = async (sessionId: string) => {
    const result = await adminRequest<{ journey: JourneyRow[] }>(`/respondents/${sessionId}/journey`);
    setJourney({ sessionId, rows: result.journey });
  };

  const updateGiftStatus = async (id: number, status: string) => {
    await adminRequest(`/gifts/${id}`, { method: "PATCH", body: JSON.stringify({ status }) });
    setGifts((current) => current.map((gift) => gift.id === id ? { ...gift, status } : gift));
  };

  if (loading) return <div className="admin-state"><span className="spinner" />Loading research dashboard…</div>;
  if (error) {
    return (
      <div className="admin-state error-state">
        <h1>Dashboard access required</h1>
        <p>{error}</p>
        <p>Enter the protected dashboard key, or sign in through Cloudflare Access after a custom domain is attached.</p>
        <div className="local-admin-form">
          <label>Admin access key<input type="password" value={localKey} onChange={(event) => setLocalKey(event.target.value)} /></label>
          <button onClick={() => { sessionStorage.setItem("swmb_local_admin_key", localKey); window.location.reload(); }}>Open dashboard</button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-shell">
      <aside className="admin-sidebar">
        <img src="/swmb-logo.png" alt="SWMB" />
        <p>Warranty research</p>
        <nav aria-label="Dashboard sections">
          {(["overview", "respondents", "responses", "contacts", "gifts"] as const).map((name) => (
            <button key={name} className={tab === name ? "active" : ""} onClick={() => setTab(name)}>{name}</button>
          ))}
        </nav>
        <small>Anonymous behavior is separated from contact records.</small>
      </aside>
      <main className="admin-main">
        <header className="admin-header">
          <div><span>Research dashboard</span><h1>Warranty Claim Copilot</h1></div>
          <span className="live-badge">● Live data</span>
        </header>

        {tab === "overview" && summary && (
          <>
            <section className="metric-grid">
              {(Object.keys(summary.totals) as Array<keyof Summary["totals"]>).map((key) => (
                <article key={key}><span>{metricLabels[key]}</span><strong>{key === "completionRate" ? `${summary.totals[key]}%` : summary.totals[key]}</strong></article>
              ))}
            </section>
            <div className="dashboard-grid">
              <section className="dashboard-card">
                <h2>Results by outreach variant</h2>
                <table><thead><tr><th>Variant</th><th>Visits</th><th>Starts</th><th>Completed</th><th>Rate</th></tr></thead>
                  <tbody>{summary.byVariant.map((row) => <tr key={row.variant}><td><span className="variant-chip">{row.variant}</span></td><td>{row.visits}</td><td>{row.starts}</td><td>{row.completions}</td><td>{row.completionRate}%</td></tr>)}</tbody>
                </table>
              </section>
              <section className="dashboard-card">
                <h2>Demo engagement</h2>
                <div className="bar-list">{summary.demo.map((row) => <div key={row.milestone}><span>{row.milestone}</span><div><i style={{ width: `${Math.min(100, summary.totals.demoStarts ? (row.count / summary.totals.demoStarts) * 100 : 0)}%` }} /></div><strong>{row.count}</strong></div>)}</div>
              </section>
              <section className="dashboard-card wide">
                <h2>Drop-off by question</h2>
                <div className="dropoff-list">{summary.dropoff.map((row) => <div key={row.questionId}><code>{row.questionId}</code><span>{row.views} viewed</span><span>{row.answers} answered</span><span>{row.skips} skipped</span></div>)}</div>
              </section>
              <section className="dashboard-card">
                <h2>Concept reactions</h2>
                <div className="reaction-list">{summary.reactions.map((row) => <div key={`${row.questionId}-${row.answer}`}><span>{row.answer}</span><strong>{row.count}</strong></div>)}</div>
              </section>
              <section className="dashboard-card">
                <h2>Next-step interest</h2>
                <div className="reaction-list">{summary.interests.map((row) => <div key={row.answer}><span>{row.answer}</span><strong>{row.count}</strong></div>)}</div>
              </section>
            </div>
          </>
        )}

        {tab === "respondents" && (
          <section className="dashboard-card table-card">
            <div className="table-toolbar"><div><h2>Anonymous respondents</h2><p>Each row is one random survey session.</p></div><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search session, variant, or status" /></div>
            <table><thead><tr><th>Session</th><th>Variant</th><th>Status</th><th>Furthest step</th><th>Started</th><th>Last seen</th><th>Journey</th></tr></thead>
              <tbody>{filteredRespondents.map((row) => <tr key={row.session_id}><td><code>{row.session_id.slice(0, 12)}…</code></td><td>{row.variant}</td><td><span className={`status status-${row.status}`}>{row.status.replace("_", " ")}</span></td><td>{row.furthest_step}</td><td>{new Date(row.created_at).toLocaleString()}</td><td>{new Date(row.last_seen_at).toLocaleString()}</td><td><button className="table-action" onClick={() => void openJourney(row.session_id)}>View</button></td></tr>)}</tbody>
            </table>
            {journey && <div className="journey-panel"><div><h3>Anonymous journey</h3><code>{journey.sessionId}</code><button onClick={() => setJourney(null)}>Close</button></div><ol>{journey.rows.map((row, index) => <li key={`${row.occurred_at}-${index}`}><time>{new Date(row.occurred_at).toLocaleString()}</time><strong>{row.event_name.replaceAll("_", " ")}</strong><span>{row.question_id ?? ""}{row.step_index !== null ? ` · step ${row.step_index}` : ""}</span></li>)}</ol></div>}
          </section>
        )}

        {tab === "responses" && (
          <section className="dashboard-card table-card sensitive-card">
            <div className="sensitive-note">Research responses, including private open text. These records stay in D1 and never go to PostHog.</div>
            <h2>Every saved response</h2>
            <table><thead><tr><th>Session</th><th>Variant</th><th>Question</th><th>Saved response</th><th>Revisions</th><th>Updated</th></tr></thead>
              <tbody>{responses.map((row) => <tr key={`${row.session_id}-${row.question_id}`}><td><code>{row.session_id.slice(0, 10)}…</code></td><td>{row.variant}</td><td><code>{row.question_id}</code></td><td className="response-cell">{row.is_skipped ? <em>Skipped</em> : formatAnswer(row.question_id, row.answer_json)}</td><td>{row.revision_count}</td><td>{new Date(row.updated_at).toLocaleString()}</td></tr>)}</tbody>
            </table>
          </section>
        )}

        {tab === "contacts" && (
          <section className="dashboard-card table-card sensitive-card">
            <div className="sensitive-note">Sensitive contact information — never sent to PostHog.</div>
            <h2>Respondents requesting follow-up</h2>
            <table><thead><tr><th>Name</th><th>Company</th><th>Contact</th><th>Method</th><th>Consent</th><th>Marketing</th></tr></thead>
              <tbody>{contacts.map((row) => <tr key={row.session_id}><td>{row.name}</td><td>{row.company}</td><td>{row.follow_up_email || row.phone}</td><td>{row.preferred_method}</td><td>{row.research_consent ? "Yes" : "No"}</td><td>{row.marketing_consent ? "Yes" : "No"}</td></tr>)}</tbody>
            </table>
          </section>
        )}

        {tab === "gifts" && (
          <section className="dashboard-card table-card sensitive-card">
            <div className="sensitive-note">One $10 gift card per company. Claims require manual fulfillment.</div>
            <h2>Gift-card claims</h2>
            <table><thead><tr><th>Company</th><th>Name</th><th>Gift email</th><th>Status</th><th>Claimed</th><th>Fulfilled</th></tr></thead>
              <tbody>{gifts.map((row) => <tr key={row.id}><td>{row.company}</td><td>{row.name}</td><td>{row.gift_email}</td><td><select className={`gift-status status-${row.status}`} value={row.status} onChange={(event) => void updateGiftStatus(row.id, event.target.value)}><option value="pending">Pending</option><option value="approved">Approved</option><option value="duplicate">Duplicate</option><option value="ineligible">Ineligible</option><option value="sent">Sent</option><option value="failed">Failed</option></select></td><td>{new Date(row.created_at).toLocaleString()}</td><td>{row.fulfilled_at ? new Date(row.fulfilled_at).toLocaleString() : "—"}</td></tr>)}</tbody>
            </table>
          </section>
        )}
      </main>
    </div>
  );
}

function formatAnswer(questionId: string, raw: string | null): string {
  if (raw === null) return "—";
  try {
    const parsed = JSON.parse(raw) as unknown;
    const options = warrantySurvey.find((step) => step.id === questionId)?.options ??
      (questionId === "priority" ? warrantySurvey.find((step) => step.id === "problems")?.options : []) ?? [];
    const label = (value: string) => options.find((option) => option.id === value)?.label ?? value;
    return Array.isArray(parsed) ? parsed.map((value) => label(String(value))).join(", ") : label(String(parsed));
  } catch {
    return raw;
  }
}
